import type { AtsJobVacancy } from '@/generated/graphql/types';
import { getCurrencySymbol } from '@/shared/components/candidate-form/utils';
import { useGetCountries } from '@/shared/hooks/use-get-countries';
import { Editor } from '@crehana/compass.editor';
import Calendar from '@crehana/compass.icons.outline/Calendar';
import { LazyImage } from '@crehana/compass.ui';
import { Button } from '@crehana/compass.ui/button';
import { Tag } from '@crehana/compass.ui/tag';
import { useTranslation } from '@crehana/i18n';
import { CapitalizeFirstLetter } from '@crehana/utils';
import { clsx } from 'clsx';
import { type Locale, format } from 'date-fns';
import enUS from 'date-fns/locale/en-US';
import es from 'date-fns/locale/es';
import ptBR from 'date-fns/locale/pt-BR';
import { useMemo } from 'react';
import { useAddResourceBundle } from '../i18n/use-add-resource-bundle';
import { useTranslationSharedCandidateForm } from '../i18n/use-translations';
import {
  contractOptions,
  workModeOptions,
  workScheduleOptions,
} from '../label-options';

//Map the language code to the date-fns locale
const localeMap: Record<string, Locale> = {
  es,
  en: enUS,
  pt: ptBR,
};

type TDetailsProps = {
  type: 'internal' | 'external';
  name: string;
  logo: string;
  jobVacancyPublicInfo: AtsJobVacancy;
};

export const Details = ({
  type,
  name,
  logo,
  jobVacancyPublicInfo,
}: TDetailsProps) => {
  useAddResourceBundle();
  const { t } = useTranslationSharedCandidateForm();

  //Get the language code
  const { languageCode } = useTranslation();
  //Get the countries
  const { data } = useGetCountries({}, !jobVacancyPublicInfo?.country);
  //Get the name of the country
  const nameCountry = useMemo(() => {
    return data?.countries?.find(
      country => country.alpha_2 === jobVacancyPublicInfo?.country,
    )?.name;
  }, [data, jobVacancyPublicInfo?.country]);

  const locale = localeMap[languageCode] || es;

  //Handle the apply click
  const handleApplyClick = () => {
    const applicationForm = document.getElementById('application-form');
    if (applicationForm) {
      applicationForm.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div>
      {/* Job vacancy details */}
      <div
        className={clsx(
          type === 'internal'
            ? 'tw-w-full tw-px-0 tw-pt-3.2 tw-pb-2'
            : 'tw-w-full tw-px-3.2 md:tw-px-[9.2rem] lg:tw-px-[32.4rem] tw-py-4 md:tw-py-4.8',
        )}
        style={
          type === 'internal'
            ? {
                background: '#FFFFFF',
              }
            : {
                background:
                  'linear-gradient(90deg, #F2F3F5 0%, rgba(240, 242, 255, 0.90) 100%)',
              }
        }
      >
        {/* Logo */}
        {type === 'external' && (
          <div className="tw-flex tw-items-center">
            <LazyImage
              imgStyle={{
                objectFit: 'contain',
                objectPosition: 'center',
              }}
              className="tw-h-5.6 tw-w-5.6 tw-mr-1.6"
              imgClassName="w-full object-contain"
              src={logo ?? ''}
              alt="logo_org"
              imgxParams={{
                w: '560px',
                h: '560px',
              }}
            />
            <div className="tw-flex tw-flex-col tw-gap-0.4">
              {/* Organization name */}
              <h1 className="tw-font-subtitle4">{name}</h1>
              {/* Published date */}
              {jobVacancyPublicInfo?.published_at && (
                <p className="tw-font-body4 tw-text-neutral-light-700">
                  {t('PUBLISHED', {
                    interpolation: {
                      datePublished: format(
                        new Date(jobVacancyPublicInfo?.published_at ?? ''),
                        "d 'de' MMMM yyyy",
                        {
                          locale,
                        },
                      ),
                    },
                  })}
                </p>
              )}
            </div>
          </div>
        )}
        {/* Job vacancy title */}
        <div className="tw-flex tw-flex-col">
          <div className="tw-w-full tw-flex tw-items-center">
            <p
              className={clsx(
                type === 'internal'
                  ? 'tw-cds-font-heading-01 tw-my-0.8'
                  : 'tw-font-h2 tw-font-semibold tw-my-2.4',
              )}
            >
              {jobVacancyPublicInfo?.title}
            </p>

            {/* Deadline date */}
            {type === 'internal' && jobVacancyPublicInfo?.deadline && (
              <div className="tw-flex tw-items-center tw-flex-shrink-0">
                <Tag variant="text" className="tw-ml-1.2 tw-cds-text-brand">
                  <div className="tw-flex tw-items-center">
                    <Calendar size={16} className="tw-mr-0.4" />
                    {t('DEADLINE', {
                      interpolation: {
                        deadlineDate: format(
                          new Date(jobVacancyPublicInfo.deadline),
                          "d 'de' MMMM yyyy",
                          {
                            locale,
                          },
                        ),
                      },
                    })}
                  </div>
                </Tag>

                {/* Published date */}
                <p className="tw-cds-font-body-01 tw-text-neutral-light-700 tw-ml-1.2">
                  {t('PUBLISHED', {
                    interpolation: {
                      datePublished: format(
                        new Date(jobVacancyPublicInfo?.published_at ?? ''),
                        "d 'de' MMMM yyyy",
                        {
                          locale,
                        },
                      ),
                    },
                  })}
                </p>
              </div>
            )}
          </div>
          {/* Job vacancy location, work mode, work schedule, contract type and work team */}
          <div
            className={clsx(
              'tw-flex tw-flex-row tw-flex-wrap tw-gap-0.8 ',
              type === 'internal'
                ? 'tw-cds-font-body-02 tw-cds-text-subtle'
                : 'tw-font-body3',
            )}
          >
            {/* Location */}
            <p>{jobVacancyPublicInfo?.show_location && nameCountry}</p>
            {/* Work mode */}
            <p>
              {jobVacancyPublicInfo?.work_mode &&
                jobVacancyPublicInfo.show_work_mode &&
                `(${workModeOptions(t)[jobVacancyPublicInfo.work_mode]?.label})`}
            </p>
            {/* Work schedule */}

            {jobVacancyPublicInfo?.workday &&
              jobVacancyPublicInfo.show_workday && (
                <>
                  <p>{t('SPACE')}</p>
                  <p>
                    {
                      workScheduleOptions(t)[jobVacancyPublicInfo?.workday]
                        ?.label
                    }
                  </p>
                </>
              )}

            {/* Contract type */}
            {jobVacancyPublicInfo?.contract_type &&
              jobVacancyPublicInfo.show_contract_type && (
                <>
                  <p>{t('SPACE')}</p>
                  <p>
                    {
                      contractOptions(t)[jobVacancyPublicInfo?.contract_type]
                        ?.label
                    }
                  </p>
                </>
              )}
            {/* Work team */}
            {jobVacancyPublicInfo?.work_team?.area?.name &&
              jobVacancyPublicInfo.show_work_team_id && (
                <>
                  <p>{t('SPACE')}</p>
                  <p>
                    {CapitalizeFirstLetter(
                      jobVacancyPublicInfo?.work_team?.area?.name.toLowerCase(),
                    )}
                  </p>
                </>
              )}
          </div>
          {/* Salary */}
          <div className="tw-flex tw-mt-1.2">
            {jobVacancyPublicInfo?.show_salary && (
              <p
                className={clsx(
                  type === 'internal'
                    ? 'tw-cds-font-body-02 tw-cds-text-subtle'
                    : 'tw-font-body3',
                )}
              >
                💵 {jobVacancyPublicInfo?.salary}
                {jobVacancyPublicInfo?.currency}
              </p>
            )}
          </div>
          {/* Extra fields */}
          {jobVacancyPublicInfo &&
            jobVacancyPublicInfo?.extra_field_info &&
            jobVacancyPublicInfo?.extra_field_info.length > 0 && (
              <div className="tw-flex tw-flex-wrap tw-mt-2.4 tw-border-t tw-border-solid tw-border-neutral-light-500 tw-pt-1.6">
                {jobVacancyPublicInfo.extra_field_info
                  .filter(field => !field.is_hidden)
                  .sort((a, b) => a.sort_order - b.sort_order)
                  .map((field, index) => {
                    if (field.content) {
                      return (
                        <div className="tw-flex tw-flex-wrap" key={field.id}>
                          <p
                            className={clsx(
                              'tw-mr-0.4',
                              type === 'internal'
                                ? 'tw-cds-font-body-02 tw-cds-text-subtle'
                                : 'tw-font-body3',
                            )}
                          >
                            {field.name}
                            {':'}
                          </p>
                          <p
                            className={clsx(
                              type === 'internal'
                                ? 'tw-cds-font-body-02 tw-cds-text-subtle'
                                : 'tw-font-body3',
                            )}
                          >
                            {getCurrencySymbol(
                              field.field_type,
                              field.currency,
                            )}
                            {field.content}
                          </p>
                          {jobVacancyPublicInfo?.extra_field_info &&
                            index !==
                              jobVacancyPublicInfo?.extra_field_info.filter(
                                field => !field.is_hidden,
                              ).length -
                                1 &&
                            jobVacancyPublicInfo?.extra_field_info.filter(
                              field => !field.is_hidden,
                            ).length > 1 && (
                              <p
                                className={clsx(
                                  'tw-mx-0.8',
                                  type === 'internal'
                                    ? 'tw-cds-font-body-02 tw-cds-text-subtle'
                                    : 'tw-font-body3',
                                )}
                              >
                                {t('SPACE')}
                              </p>
                            )}
                        </div>
                      );
                    }
                    return null;
                  })}
              </div>
            )}
          {/* Apply button */}
          {type === 'external' && (
            <div className="tw-flex tw-mt-4">
              <Button
                variant="primary"
                type="button"
                onClick={handleApplyClick}
              >
                {t('APPLY')}
              </Button>
            </div>
          )}
        </div>
      </div>
      {/**The following class was added so that the editor does not have borders [&>div>div>div]:tw-border-none since this is not allowing the class for the div levels */}{' '}
      {type === 'external' && (
        <div className="tw-w-full tw-px-3.2 md:tw-px-[9.2rem] lg:tw-px-[32.4rem] tw-pt-5.6 tw-pb-6.4 tw-bg-neutral-light-100 tw-border-none [&>div>div>div]:tw-border-none">
          {/* Job vacancy description */}
          {jobVacancyPublicInfo?.description && (
            <Editor
              initialState={jobVacancyPublicInfo.description.toString()}
              layout={{ toolbar: false }}
              contentEditable={false}
              onChange={() => undefined}
              className="tw-w-full tw-h-auto tw-border-none tw-p-0 tw-overflow-hidden tw-font-pop"
            />
          )}
        </div>
      )}
    </div>
  );
};
